import {
  DatesSetArg,
  EventClickArg,
  EventSourceInput,
} from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Messages } from '../constants';
import { useAlert } from '../context/AlertContext';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { initEvent } from '../Interfaces/Calendar/ICalendarEvent';
import { ICalendarEventForm } from '../Interfaces/Calendar/ICalendarEventForm';
import { CalendarWrapper } from './CalendarWrapper';
import { CalendarConnectionsView } from './CalendarConnectionsView';
import { CalendarSidebar } from './CalendarSidebar';
import { EventFormDialog } from './EventFormDialog';
import { transformEvent } from './utilities';
import {
  useGetCalendarEvents,
  useRefreshCalendarCache,
} from '../hooks/calendarHooks';
import { useCalendarConnections } from '../hooks/reactQuerys/CalendarConnectionQueries';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

// Die Backend-Antwort hängt an jedem Event ein `_connection`-Block — dort
// sieht das Frontend, zu welcher Calendar-Connection ein Event gehört, und
// kann es entsprechend filtern und einfärben.
type EventWithSource = ICalendarEventForm & {
  _connection?: {
    idUserCalendarConnection?: number;
    Label?: string;
    Provider?: string;
    color?: string | null;
  };
};

const VISIBILITY_KEY_PREFIX = 'dino.calendar.visibleConnections.v1.';

// Lesbare Schriftfarbe (schwarz/weiß) zur HEX-Hintergrundfarbe ableiten.
const contrastTextColor = (hex?: string): string => {
  if (!hex) return '#fff';
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return '#fff';
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  // YIQ-Helligkeit (W3C-Empfehlung)
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#1a1a1a' : '#fff';
};

const fallbackColor = (id?: number): string => {
  const palette = [
    '#1f6feb',
    '#16a34a',
    '#dc2626',
    '#9333ea',
    '#ea580c',
    '#0891b2',
    '#db2777',
    '#65a30d',
  ];
  return palette[(id ?? 0) % palette.length];
};

// Default-Sichtfenster beim Erstabruf (Tage relativ zu heute). Bewusst klein,
// damit grosse Kalender (viele Serien-Termine) nicht ins Timeout laufen —
// groessere Zeitraeume werden beim Blaettern nachgeladen.
const DEFAULT_BEFORE_DAYS = 30;
const DEFAULT_AFTER_DAYS = 90;
// Puffer um den sichtbaren Bereich, damit nicht jeder Navigationsschritt einen
// Nachlade-Request ausloest.
const RANGE_BUFFER_DAYS = 14;

const addDays = (d: Date, days: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
};

export const CalendarMain: React.FC = () => {
  const { showAlert } = useAlert();
  const userId = Number(localStorage.getItem('idUser'));
  const visibilityKey = `${VISIBILITY_KEY_PREFIX}${userId}`;

  const [isMount, setIsMount] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEventForm>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [events, setEvents] = useState<EventWithSource[]>();

  // Zwei separate Modi für den Manage-Dialog: Übersicht oder direkt der
  // „Hinzufügen"-Wizard.
  const [manageOpen, setManageOpen] = useState<false | 'list' | 'add'>(false);

  // Sichtbarkeits-Set persistieren — User möchte nicht jedes Mal wieder
  // ankreuzen müssen.
  const [visibleIds, setVisibleIds] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem(visibilityKey);
      if (raw) {
        const arr = JSON.parse(raw) as number[];
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch {
      /* ignore */
    }
    return new Set<number>();
  });

  // Aktuell angefragtes Sichtfenster. Waechst nur (schrumpft nie), damit
  // bereits geladene Zeitraeume beim Zurueckblaettern nicht erneut gezogen
  // werden muessen.
  const [range, setRange] = useState<{ start: string; end: string }>(() => {
    const now = new Date();
    return {
      start: addDays(now, -DEFAULT_BEFORE_DAYS).toISOString(),
      end: addDays(now, DEFAULT_AFTER_DAYS).toISOString(),
    };
  });

  const { connections, isLoadingConnections, refetchConnections } =
    useCalendarConnections(userId);

  const {
    calendarEvents,
    isFetchingCalendarEvents,
    isErrorCalendarEvents,
    refetchCalendarEvents,
  } = useGetCalendarEvents(userId, range.start, range.end);

  // Force-Refresh: leert serverseitig den Event-Cache und zieht die
  // externen Provider live ab. Wird vom Sidebar-Refresh-Button benutzt,
  // damit der Notar manuell sicherstellen kann, dass alle Termine aktuell
  // sind.
  const { refreshCalendarCache, isRefreshing } =
    useRefreshCalendarCache(userId);

  // Mandanten fuer die Backref-Aufloesung der Attendees laden. Ohne
  // searchUri ist der Hook disabled — daher liefern wir einen Stub mit
  // grosser Seite, damit alle Mandanten geladen werden.
  const { legalClients } = useGetLegalClients('page=1&rowsPerPage=1000');

  // Erste Initialisierung der Sichtbarkeit: alle Connections aktiv und
  // sichtbar, sobald die Liste vom Backend zurückkommt.
  useEffect(() => {
    if (!connections || connections.length === 0) return;
    setVisibleIds(prev => {
      // Wir mergen: bestehende Auswahl bleibt erhalten, neu hinzugekommene
      // Connections werden per Default sichtbar geschaltet.
      const next = new Set(prev);
      let changed = false;
      // Connection-Set, das aktuell existiert
      const existing = new Set(
        connections.map(c => c.idUserCalendarConnection)
      );
      // Verschwundene Connections aus der Sichtbarkeit räumen
      Array.from(next).forEach(id => {
        if (!existing.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      // Neue Connections per Default einblenden — aber nur wenn noch nie
      // etwas gespeichert wurde, sonst respektieren wir die User-Auswahl.
      const wasInitiallyEmpty =
        prev.size === 0 && !localStorage.getItem(visibilityKey);
      if (wasInitiallyEmpty) {
        for (const conn of connections) {
          if (conn.IsActive) {
            next.add(conn.idUserCalendarConnection);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [connections, visibilityKey]);

  // Sichtbarkeit persistieren
  useEffect(() => {
    try {
      localStorage.setItem(
        visibilityKey,
        JSON.stringify(Array.from(visibleIds))
      );
    } catch {
      /* ignore */
    }
  }, [visibleIds, visibilityKey]);

  // Events transformieren, sobald Daten da sind
  useEffect(() => {
    if (calendarEvents === undefined) return;
    // legalClients-Hook ist disabled, solange kein searchUri übergeben wird —
    // für die reine Anzeige reicht eine leere Liste.
    const clients = legalClients?.results ?? [];
    setEvents(
      calendarEvents.map(e => transformEvent(e, clients) as EventWithSource)
    );
  }, [
    legalClients,
    calendarEvents,
    isFetchingCalendarEvents,
    isErrorCalendarEvents,
  ]);

  // Map idUserCalendarConnection → Farbe (Backend-color > deterministisch)
  const colorByConnection = useMemo(() => {
    const m = new Map<number, string>();
    for (const conn of connections) {
      const cfgColor = conn.Config?._color;
      m.set(
        conn.idUserCalendarConnection,
        cfgColor || fallbackColor(conn.idUserCalendarConnection)
      );
    }
    return m;
  }, [connections]);

  // FullCalendar-Events bauen, gefiltert + farbig
  const displayedEvents = useMemo<EventSourceInput | undefined>(() => {
    if (!events) return undefined;
    return events
      .filter(event => {
        const idConn = event._connection?.idUserCalendarConnection;
        // Events ohne Connection (alte Daten) immer einblenden.
        if (idConn === undefined || idConn === null) return true;
        return visibleIds.has(idConn);
      })
      .map(event => {
        const start = event.start.dateTime;
        let end = event.end.dateTime;
        if (event.isAllDay) {
          const endDate = new Date(event.end.dateTime);
          endDate.setDate(endDate.getDate() + 1);
          end = endDate.toISOString();
        }
        const idConn = event._connection?.idUserCalendarConnection;
        const bg =
          idConn !== undefined ? colorByConnection.get(idConn) : undefined;
        const fg = contrastTextColor(bg);
        return {
          id: event.id,
          title: event.subject,
          start,
          end,
          allDay: event.isAllDay,
          backgroundColor: bg,
          borderColor: bg,
          textColor: fg,
          extendedProps: {
            location: event.location?.displayName || '',
            description: event.body?.content || '',
            connectionLabel: event._connection?.Label,
          },
        };
      });
  }, [events, visibleIds, colorByConnection]);

  // FullCalendar meldet bei jeder Navigation / Ansichts-Aenderung den
  // sichtbaren Bereich. Wir vergroessern das getrackte Fenster (inkl. Puffer),
  // sobald der User ueber den bisher geladenen Bereich hinaus blaettert — der
  // Hook laedt dann per Range-Query nur die fehlende Rand-Scheibe nach.
  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    setRange(prev => {
      const prevStart = new Date(prev.start);
      const prevEnd = new Date(prev.end);
      const wantStart = addDays(arg.start, -RANGE_BUFFER_DAYS);
      const wantEnd = addDays(arg.end, RANGE_BUFFER_DAYS);
      const newStart = wantStart < prevStart ? wantStart : prevStart;
      const newEnd = wantEnd > prevEnd ? wantEnd : prevEnd;
      if (
        newStart.getTime() === prevStart.getTime() &&
        newEnd.getTime() === prevEnd.getTime()
      ) {
        return prev; // Fenster deckt den Bereich bereits ab — kein Refetch
      }
      return { start: newStart.toISOString(), end: newEnd.toISOString() };
    });
  }, []);

  const onClickDate = useCallback((info: { dateStr: string }) => {
    setSelectedDate(info.dateStr);
    setSelectedEvent(undefined);
    setIsMount(true);
  }, []);

  const onClick = useCallback(
    ({ event: { id } }: EventClickArg) => {
      setSelectedEvent(events?.find(e => e.id === id));
      setIsMount(true);
    },
    [events]
  );

  const unmount = useCallback(
    (editedEvent?: ICalendarEventForm, isDeleted?: boolean) => {
      if (isDeleted) {
        setEvents(prev => prev?.filter(c => c.id !== editedEvent?.id));
      } else if (editedEvent && calendarEvents !== undefined) {
        setEvents(prev => {
          // Bestehender Eintrag? Dann updaten. Sonst — typischer Fall
          // beim Anlegen: in die Liste aufnehmen, damit der Termin
          // sofort im Kalender erscheint, ohne dass der User
          // „Aktualisieren" druecken muss.
          const exists = prev?.some(c => c.id === editedEvent.id);
          if (exists) {
            return prev?.map(c =>
              c.id === editedEvent.id
                ? ({ ...c, ...editedEvent } as EventWithSource)
                : c
            );
          }
          return [...(prev ?? []), editedEvent as EventWithSource];
        });
      }
      setIsMount(false);
      setSelectedEvent(undefined);
      setSelectedDate(undefined);
    },
    [calendarEvents]
  );

  const onReload = useCallback(async () => {
    // Erst serverseitigen Cache neu fuellen (= externe Kalender-Provider
    // anstossen), dann lokale Connections + Events neu laden.
    try {
      await refreshCalendarCache();
    } catch {
      /* Cache-Refresh-Fehler werden im Backend pro Connection geloggt
         und im Cache-Status sichtbar — die UI faellt einfach auf den
         vorhandenen Cache zurueck. */
    }
    await Promise.all([refetchCalendarEvents(), refetchConnections()]);
    showAlert({ text: Messages.ReloadSuccessfully });
  }, [
    refreshCalendarCache,
    refetchCalendarEvents,
    refetchConnections,
    showAlert,
  ]);

  const toggleVisible = useCallback((id: number) => {
    setVisibleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    setVisibleIds(new Set(connections.map(c => c.idUserCalendarConnection)));
  }, [connections]);

  const hideAll = useCallback(() => {
    setVisibleIds(new Set());
  }, []);

  const handleConnectionsChanged = useCallback(() => {
    void refetchConnections();
    void refetchCalendarEvents();
  }, [refetchConnections, refetchCalendarEvents]);

  if (isLoadingConnections) return <LoadingSpinner />;

  // Ohne Connections direkt in die Verwaltung springen (Onboarding).
  if (connections.length === 0) {
    return (
      <CalendarConnectionsView
        userId={userId}
        onConnectionsChanged={handleConnectionsChanged}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        // calc statt 100% damit AppShell-Padding/Header berücksichtigt sind;
        // overflow:hidden verhindert äußere Scrollbalken — Scrollen passiert
        // intern in FullCalendars TimeGrid.
        height: 'calc(100vh - 96px)',
        minHeight: 560,
        overflow: 'hidden',
      }}
    >
      <CalendarSidebar
        connections={connections}
        visibleIds={visibleIds}
        onToggle={toggleVisible}
        onShowAll={showAll}
        onHideAll={hideAll}
        onAdd={() => setManageOpen('add')}
        onManage={() => setManageOpen('list')}
        onReload={onReload}
        isReloading={isFetchingCalendarEvents || isRefreshing}
      />

      {/* Rechter Hauptbereich. minHeight:0 ist Pflicht, damit das Flex-Child
          schrumpfen darf — ohne diesen Trick streckt FullCalendar seinen
          Container ins Unendliche und verliert das TimeGrid. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          gap: 1,
        }}
      >
        {isErrorCalendarEvents && (
          <Alert severity='error'>
            Mindestens ein Kalender konnte nicht geladen werden. Über
            „Verwalten" in der Sidebar können Sie die Verbindungen prüfen.
          </Alert>
        )}
        <CalendarWrapper>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView='timeGridWeek'
            events={displayedEvents}
            locale={deLocale}
            datesSet={handleDatesSet}
            dateClick={onClickDate}
            eventClick={onClick}
            headerToolbar={{
              left: 'prev,next today newEvent',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            buttonText={{
              today: 'Heute',
              month: 'Monat',
              week: 'Woche',
              day: 'Tag',
            }}
            firstDay={1}
            height='100%'
            expandRows
            stickyHeaderDates
            nowIndicator
            eventDisplay='block'
            dayMaxEvents={4}
            slotMinTime='06:00:00'
            slotMaxTime='22:00:00'
            slotDuration='00:30:00'
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            allDaySlot
            allDayText='Ganztags'
            scrollTime='08:00:00'
            customButtons={{
              newEvent: {
                text: 'Neues Ereignis',
                click: () => setIsMount(true),
              },
            }}
          />
        </CalendarWrapper>
      </Box>

      {/*
        Verwaltungs-Dialog: NUR im Modus 'list' rendern wir die volle
        Connections-Verwaltung als Dialog. Im 'add'-Modus zeigen wir die
        ConnectionsView versteckt — sie oeffnet ihren eigenen
        AddConnectionDialog (Modal). Damit erscheint NICHT zusaetzlich
        die Verwaltungsliste, wenn der Nutzer „Kalender hinzufuegen"
        klickt.
      */}
      {manageOpen === 'list' && (
        <Dialog
          open
          onClose={() => setManageOpen(false)}
          maxWidth='md'
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            Kalenderverbindungen
            <IconButton
              onClick={() => setManageOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
              size='small'
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <CalendarConnectionsView
              userId={userId}
              onConnectionsChanged={handleConnectionsChanged}
            />
          </DialogContent>
        </Dialog>
      )}

      {manageOpen === 'add' && (
        <Box sx={{ display: 'none' }}>
          <CalendarConnectionsView
            userId={userId}
            initialAddOpen
            onAddDialogClose={() => setManageOpen(false)}
            onConnectionsChanged={() => {
              handleConnectionsChanged();
              setManageOpen(false);
            }}
          />
        </Box>
      )}

      {isMount && (
        <EventFormDialog
          userId={userId}
          defaultValues={
            selectedEvent ??
            initEvent(selectedDate ? new Date(selectedDate) : new Date())
          }
          unmount={unmount}
        />
      )}
    </Box>
  );
};
